import { type Word, wordsIn } from "@/lib/tool-kit/style-checkers/a-words";

/**
 * Numbers in words: reading "twenty-five" and "two million" as values, and writing 25 as
 * "twenty-five". American style (no "and" inside a number), up to 999,999,999.
 */

const UNITS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
] as const;

const TENS = [
  "",
  "",
  "twenty",
  "thirty",
  "forty",
  "fifty",
  "sixty",
  "seventy",
  "eighty",
  "ninety",
] as const;

const SCALES: readonly [number, string][] = [
  [1_000_000_000, "billion"],
  [1_000_000, "million"],
  [1000, "thousand"],
];

/** The largest number written out or read back. */
export const MAX_WORD_NUMBER = 999_999_999;

function belowThousand(n: number): string {
  const parts: string[] = [];
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds > 0) {
    parts.push(`${UNITS[hundreds]} hundred`);
  }
  if (rest > 0) {
    if (rest < 20) {
      parts.push(UNITS[rest]);
    } else {
      const ones = rest % 10;
      parts.push(
        ones === 0
          ? TENS[Math.floor(rest / 10)]
          : `${TENS[Math.floor(rest / 10)]}-${UNITS[ones]}`
      );
    }
  }
  return parts.join(" ");
}

/** 25 → "twenty-five", 1200 → "one thousand two hundred". Integers 0–999,999,999 only. */
export function numberToWords(n: number): string {
  if (!Number.isInteger(n) || n < 0 || n > MAX_WORD_NUMBER) {
    return "";
  }
  if (n === 0) {
    return "zero";
  }
  const parts: string[] = [];
  let rest = n;
  for (const [size, name] of SCALES) {
    if (rest >= size) {
      parts.push(`${belowThousand(Math.floor(rest / size))} ${name}`);
      rest %= size;
    }
  }
  if (rest > 0) {
    parts.push(belowThousand(rest));
  }
  return parts.join(" ");
}

const ORDINAL_IRREGULAR: Readonly<Record<string, string>> = {
  one: "first",
  two: "second",
  three: "third",
  five: "fifth",
  eight: "eighth",
  nine: "ninth",
  twelve: "twelfth",
};

const RE_Y_END = /y$/;

function ordinalWord(word: string): string {
  const irregular = ORDINAL_IRREGULAR[word];
  if (irregular) {
    return irregular;
  }
  return RE_Y_END.test(word) ? `${word.slice(0, -1)}ieth` : `${word}th`;
}

/** 21 → "twenty-first", 100 → "one hundredth". */
export function ordinalToWords(n: number): string {
  const words = numberToWords(n);
  if (!words) {
    return "";
  }
  const cut = Math.max(words.lastIndexOf(" "), words.lastIndexOf("-"));
  return `${words.slice(0, cut + 1)}${ordinalWord(words.slice(cut + 1))}`;
}

/* ------------------------------------------------------------------ reading */

type Category = "unit" | "teen" | "tens" | "hundred" | "scale" | "ordinal";

const CATEGORY = new Map<string, { category: Category; value: number }>();

for (const [value, word] of UNITS.entries()) {
  if (value >= 1) {
    CATEGORY.set(word, { category: value < 10 ? "unit" : "teen", value });
  }
}
for (const [index, word] of TENS.entries()) {
  if (index >= 2) {
    CATEGORY.set(word, { category: "tens", value: index * 10 });
  }
}
CATEGORY.set("hundred", { category: "hundred", value: 100 });
for (const [value, word] of SCALES) {
  CATEGORY.set(word, { category: "scale", value });
}

function ordinalBaseValue(n: number): number {
  return n < 20 || n % 10 === 0 ? n : n % 10;
}

/** Ordinals read as numbers too: "first" is 1, "twentieth" is 20. */
const ORDINAL_VALUES = new Map<string, number>();
for (let n = 1; n <= 100; n++) {
  const word = ordinalToWords(n);
  // Only the last word of a compound is an ordinal on its own.
  const last = word.slice(
    Math.max(word.lastIndexOf(" "), word.lastIndexOf("-")) + 1
  );
  if (!ORDINAL_VALUES.has(last)) {
    ORDINAL_VALUES.set(last, ordinalBaseValue(n));
  }
}
ORDINAL_VALUES.set("hundredth", 100);

/** Which categories may follow which in a spoken number. */
const FOLLOWS: Readonly<Record<Category, readonly Category[]>> = {
  unit: ["hundred", "scale"],
  teen: ["hundred", "scale"],
  tens: ["unit", "ordinal", "scale"],
  hundred: ["tens", "unit", "teen", "ordinal", "scale"],
  scale: ["unit", "teen", "tens", "hundred", "ordinal"],
  ordinal: [],
};

export interface NumberWordRun {
  start: number;
  end: number;
  text: string;
  /** Lowercase words in order. */
  words: string[];
  value: number;
  ordinal: boolean;
  /** The last word is "million" or "billion": 2 million is written "2 million". */
  scaleWord: string | null;
}

function categoryOf(
  word: string
): { category: Category; value: number } | null {
  const plain = CATEGORY.get(word);
  if (plain) {
    return plain;
  }
  const ordinal = ORDINAL_VALUES.get(word);
  return ordinal === undefined ? null : { category: "ordinal", value: ordinal };
}

const RE_JOINER = /^[-\s]$/;

function runValue(words: readonly string[]): number {
  let total = 0;
  let current = 0;
  for (const word of words) {
    const entry = categoryOf(word);
    if (!entry) {
      continue;
    }
    if (entry.category === "hundred") {
      current = (current || 1) * 100;
    } else if (entry.category === "scale") {
      total += (current || 1) * entry.value;
      current = 0;
    } else {
      current += entry.value;
    }
  }
  return total + current;
}

/** Whether the token after `from` joins the run: a legal next category, one space or hyphen away. */
function canJoin(
  text: string,
  tokens: readonly Word[],
  from: number,
  previous: Category
): boolean {
  const next = tokens[from + 1];
  if (!(next && RE_JOINER.test(text.slice(tokens[from].end, next.start)))) {
    return false;
  }
  const entry = categoryOf(next.lower);
  return entry !== null && FOLLOWS[previous].includes(entry.category);
}

/** Every number written in words in `text`: "five", "twenty-one", "two million". */
export function numberWordRuns(text: string): NumberWordRun[] {
  const tokens = wordsIn(text);
  const runs: NumberWordRun[] = [];
  let i = 0;
  while (i < tokens.length) {
    const first = categoryOf(tokens[i].lower);
    // "3.5 million" and "hundreds": a scale word with no number before it is not a run.
    if (!first || first.category === "scale" || first.category === "hundred") {
      i++;
      continue;
    }
    let last = i;
    let category: Category = first.category;
    while (canJoin(text, tokens, last, category)) {
      last++;
      category = (categoryOf(tokens[last].lower) as { category: Category })
        .category;
    }
    const words = tokens.slice(i, last + 1).map((token) => token.lower);
    const lastWord = words.at(-1) ?? "";
    runs.push({
      start: tokens[i].start,
      end: tokens[last].end,
      text: text.slice(tokens[i].start, tokens[last].end),
      words,
      value: runValue(words),
      ordinal: category === "ordinal",
      scaleWord:
        lastWord === "million" || lastWord === "billion" ? lastWord : null,
    });
    i = last + 1;
  }
  return runs;
}

/** 1200 → "1,200"; 2000000 with scale word "million" → "2 million". */
export function formatNumeral(value: number, scaleWord: string | null): string {
  if (scaleWord) {
    const size = scaleWord === "billion" ? 1_000_000_000 : 1_000_000;
    if (value % size === 0) {
      return `${value / size} ${scaleWord}`;
    }
  }
  return value >= 1000 ? value.toLocaleString("en-US") : String(value);
}

const RE_ORDINAL_SUFFIX = /(?:11|12|13)$/;

/** 3 → "3rd", 12 → "12th", 21 → "21st". */
export function ordinalNumeral(n: number): string {
  if (RE_ORDINAL_SUFFIX.test(String(n))) {
    return `${n}th`;
  }
  const suffix = { 1: "st", 2: "nd", 3: "rd" }[n % 10] ?? "th";
  return `${n}${suffix}`;
}
